import {
  AlertCircle,
  CheckCircle,
  InformationCircleIcon,
  XCircle,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeftIcon, CheckIcon, ClockIcon, XIcon } from 'lucide-react';
import { useWebhook, useWebhookLogs } from '@/components/features/webhooks/hooks';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

function WebhookDetailPage() {
  // @ts-expect-error - useParams type issue
  const { id } = Route.useParams<{ id: string }>();
  const { data: webhook, isLoading: isLoadingWebhook } = useWebhook(id ?? '');
  const { data: logs, isLoading: isLoadingLogs } = useWebhookLogs(id ?? '');
  const location = useLocation();

  // Check if we're on a child route (like /edit) by checking if pathname ends with /edit
  const isEditRoute = location.pathname.endsWith('/edit');

  // If on child route, render Outlet only; otherwise render detail page
  if (isEditRoute) {
    return <Outlet />;
  }

  if (isLoadingWebhook || isLoadingLogs) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!webhook) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-muted-foreground">Webhook not found</p>
          <Link to="/integrations/webhooks">
            <Button variant="outline" className="mt-4">
              Back to Webhooks
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return (
          <Badge variant="outline" className="text-green-600 border-green-600">
            <CheckIcon className="w-3 h-3 mr-1" />
            Success
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="outline" className="text-red-600 border-red-600">
            <XIcon className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="outline" className="text-yellow-600 border-yellow-600">
            <ClockIcon className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const events = typeof webhook.events === 'string' ? JSON.parse(webhook.events) : webhook.events;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/integrations/webhooks">
          <Button variant="ghost" size="icon">
            <ArrowLeftIcon className="w-4 h-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">{webhook.name}</h1>
          <p className="text-sm text-muted-foreground">{webhook.url}</p>
        </div>
        <Link to={`/integrations/webhooks/${id}/edit`}>
          <Button variant="outline">Edit</Button>
        </Link>
      </div>

      {/* Webhook Info */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Events</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{events.length}</p>
            <p className="text-xs text-muted-foreground">subscribed events</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{webhook.successRate}%</p>
            <p className="text-xs text-muted-foreground">last 10 deliveries</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <HugeiconsIcon
                icon={
                  webhook.lastStatus === 'healthy'
                    ? CheckCircle
                    : webhook.lastStatus === 'degraded'
                      ? AlertCircle
                      : XCircle
                }
                size={24}
                className={
                  webhook.lastStatus === 'healthy'
                    ? 'text-green-600'
                    : webhook.lastStatus === 'degraded'
                      ? 'text-yellow-600'
                      : 'text-red-600'
                }
              />
              <span className="capitalize">{webhook.lastStatus}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delivery Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Delivery Logs</CardTitle>
          <CardDescription>Recent webhook delivery attempts</CardDescription>
        </CardHeader>
        <CardContent>
          {!logs || logs.length === 0 ? (
            <div className="text-center py-8">
              <HugeiconsIcon
                icon={InformationCircleIcon}
                size={32}
                className="mx-auto text-muted-foreground mb-2"
              />
              <p className="text-muted-foreground">No delivery logs yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <div key={log.id} className="border-b pb-4 last:border-0 last:pb-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getStatusBadge(log.status)}
                      <span className="text-sm font-mono text-muted-foreground">
                        {log.eventType}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-xs text-muted-foreground">
                    <div>
                      <span className="font-medium">Status Code:</span> {log.statusCode || 'N/A'}
                    </div>
                    <div>
                      <span className="font-medium">Duration:</span>{' '}
                      {log.duration ? `${log.duration}ms` : 'N/A'}
                    </div>
                    <div>
                      <span className="font-medium">Attempt:</span> {log.attemptNumber}
                    </div>
                    <div>
                      <span className="font-medium">Time:</span>{' '}
                      {new Date(log.createdAt).toLocaleString()}
                    </div>
                  </div>
                  {log.errorMessage && (
                    <div className="mt-2 text-xs text-red-600 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                      {log.errorMessage}
                    </div>
                  )}
                  {log.response && (
                    <details className="mt-2">
                      <summary className="text-xs cursor-pointer text-muted-foreground hover:text-foreground">
                        View Response
                      </summary>
                      <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-x-auto">
                        {log.response}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/integrations/webhooks/$id')({
  component: WebhookDetailPage,
});
